/**
 * Contexto de Bloqueo de Pantalla
 *
 * Maneja el bloqueo/desbloqueo con PIN de 4 dígitos
 * Incluye detección de inactividad para auto-lock
 *
 * Seguridad: El estado de desbloqueo se almacena como un token HMAC
 * firmado con el hash del PIN. Manipular localStorage no permite
 * evadir el bloqueo sin conocer el PIN.
 */

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { storage } from '../utils/storage';

const LockContext = createContext();

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutos
const PIN_STORAGE_KEY = 'comanda_screen_pin';
const PIN_HASH_LENGTH = 64; // SHA-256 hex output length (legacy)
const PBKDF2_ITERATIONS = 100000;
const PBKDF2_SALT_BYTES = 16;
const UNLOCK_TOKEN_KEY = 'comanda_unlock_token';
const UNLOCK_NONCE_KEY = 'comanda_unlock_nonce';
const LOCK_TIMESTAMP_KEY = 'comanda_screen_lock_timestamp';
const PIN_CONFIGURED_KEY = 'comanda_pin_configured';
const FAILED_ATTEMPTS_KEY = 'comanda_failed_attempts';
const LOCKOUT_UNTIL_KEY = 'comanda_lockout_until';

// Legacy key — read-only for migration, then removed
const LEGACY_LOCK_STATE_KEY = 'comanda_screen_locked';

function bytesToHex(bytes) {
  return Array.from(new Uint8Array(bytes))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function hexToBytes(hex) {
  return new Uint8Array(hex.match(/.{2}/g).map(b => parseInt(b, 16)));
}

// Legacy SHA-256 hash (kept for migration compatibility)
async function hashPinLegacy(pin) {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return bytesToHex(hashBuffer);
}

// PBKDF2 with random salt — returns "salt:hash"
async function hashPin(pin, existingSalt) {
  const encoder = new TextEncoder();
  const salt = existingSalt
    ? hexToBytes(existingSalt)
    : crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_BYTES));

  const keyMaterial = await crypto.subtle.importKey(
    'raw', encoder.encode(pin), 'PBKDF2', false, ['deriveBits']
  );
  const derived = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial, 256
  );
  const saltHex = bytesToHex(salt);
  const hashHex = bytesToHex(derived);
  return `${saltHex}:${hashHex}`;
}

function isPBKDF2Format(stored) {
  return stored && stored.includes(':');
}

async function generateUnlockToken(pinHash) {
  const nonce = crypto.randomUUID();
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pinHash),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    new TextEncoder().encode(nonce)
  );
  return { nonce, token: bytesToHex(signature) };
}

async function verifyUnlockToken(pinHash, nonce, token) {
  try {
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(pinHash),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['verify']
    );
    return await crypto.subtle.verify(
      'HMAC',
      key,
      hexToBytes(token),
      new TextEncoder().encode(nonce)
    );
  } catch {
    return false;
  }
}

// Rate limiting: lockout thresholds
const LOCKOUT_THRESHOLDS = [
  { attempts: 3, lockoutMs: 30 * 1000 },      // 3 intentos → 30s
  { attempts: 6, lockoutMs: 5 * 60 * 1000 },  // 6 intentos → 5min
  { attempts: 10, lockoutMs: null },           // 10 intentos → cerrar sesión
];

export const useLock = () => {
  const context = useContext(LockContext);
  if (!context) {
    throw new Error('useLock debe ser usado dentro de un LockProvider');
  }
  return context;
};

export const LockProvider = ({ children }) => {
  // Default to locked — async verification may unlock
  const [isLocked, setIsLocked] = useState(() => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    // No PIN = no lock (needs setup)
    if (!savedPin) return false;
    // With PIN, default to locked until token is verified async
    return true;
  });
  const [hasPin, setHasPin] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(() => {
    const stored = sessionStorage.getItem(FAILED_ATTEMPTS_KEY);
    return stored ? parseInt(stored, 10) : 0;
  });
  const [lockoutUntil, setLockoutUntil] = useState(() => {
    const stored = sessionStorage.getItem(LOCKOUT_UNTIL_KEY);
    if (!stored) return null;
    const until = parseInt(stored, 10);
    return Date.now() < until ? until : null;
  });
  const [mustLogout, setMustLogout] = useState(false);
  const inactivityTimerRef = useRef(null);
  const lockoutTimerRef = useRef(null);

  // Verify unlock token on mount (async HMAC check)
  useEffect(() => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    const pinWasConfigured = storage.get(PIN_CONFIGURED_KEY) === 'true';

    // Tamper detection: PIN hash was deleted but a PIN was previously configured
    if (!savedPin && pinWasConfigured) {
      console.warn('[LockContext] PIN hash missing but was previously configured — forcing logout');
      setMustLogout(true);
      return;
    }

    setHasPin(!!savedPin);

    if (!savedPin) return;

    // Migrate from legacy boolean lock state
    const legacyLockState = storage.get(LEGACY_LOCK_STATE_KEY);
    if (legacyLockState !== null) {
      // Clean up legacy keys
      storage.remove(LEGACY_LOCK_STATE_KEY);
      if (legacyLockState === 'true') {
        // Was locked — stay locked (no unlock token)
        storage.remove(UNLOCK_TOKEN_KEY);
        storage.remove(UNLOCK_NONCE_KEY);
        return;
      }
      // Was unlocked with legacy system — stay locked now (require PIN re-entry)
      storage.remove(UNLOCK_TOKEN_KEY);
      storage.remove(UNLOCK_NONCE_KEY);
      return;
    }

    const token = storage.get(UNLOCK_TOKEN_KEY);
    const nonce = storage.get(UNLOCK_NONCE_KEY);

    // No token = locked
    if (!token || !nonce) return;

    // 24-hour safety: auto-expire unlock tokens
    const lockTimestamp = storage.get(LOCK_TIMESTAMP_KEY);
    if (lockTimestamp) {
      const hoursSinceUnlock =
        (Date.now() - parseInt(lockTimestamp)) / (1000 * 60 * 60);
      if (hoursSinceUnlock > 24) {
        storage.remove(UNLOCK_TOKEN_KEY);
        storage.remove(UNLOCK_NONCE_KEY);
        storage.remove(LOCK_TIMESTAMP_KEY);
        return;
      }
    }

    // Verify HMAC token
    verifyUnlockToken(savedPin, nonce, token).then((valid) => {
      if (valid) {
        setIsLocked(false);
      } else {
        // Tampered token — remove and stay locked
        storage.remove(UNLOCK_TOKEN_KEY);
        storage.remove(UNLOCK_NONCE_KEY);
      }
    });
  }, []);

  // Resetear el timer de inactividad
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (!isLocked && hasPin) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log('[LockContext] Auto-bloqueando por inactividad');
        storage.remove(UNLOCK_TOKEN_KEY);
        storage.remove(UNLOCK_NONCE_KEY);
        storage.remove(LOCK_TIMESTAMP_KEY);
        setIsLocked(true);
      }, INACTIVITY_TIMEOUT);
    }
  }, [isLocked, hasPin]);

  // Detectar actividad del usuario
  useEffect(() => {
    if (!hasPin || isLocked) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const onActivity = () => resetInactivityTimer();

    events.forEach(event => {
      document.addEventListener(event, onActivity);
    });

    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, onActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [hasPin, isLocked, resetInactivityTimer]);

  // Configurar PIN por primera vez
  const setPin = async (pin) => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      throw new Error('El PIN debe tener exactamente 4 dígitos');
    }
    const saltedHash = await hashPin(pin);
    storage.set(PIN_STORAGE_KEY, saltedHash);
    storage.set(PIN_CONFIGURED_KEY, 'true');
    setHasPin(true);
    console.log('[LockContext] PIN configurado exitosamente (PBKDF2)');
  };

  // Bloquear manualmente
  const lock = () => {
    if (!hasPin) {
      console.warn('[LockContext] No se puede bloquear sin PIN configurado');
      return;
    }
    console.log('[LockContext] Bloqueando pantalla manualmente');
    storage.remove(UNLOCK_TOKEN_KEY);
    storage.remove(UNLOCK_NONCE_KEY);
    storage.remove(LOCK_TIMESTAMP_KEY);
    setIsLocked(true);
  };

  // Calcular lockout basado en intentos fallidos
  const applyLockout = useCallback((attempts) => {
    sessionStorage.setItem(FAILED_ATTEMPTS_KEY, attempts.toString());
    // Buscar el threshold más alto que aplique
    for (let i = LOCKOUT_THRESHOLDS.length - 1; i >= 0; i--) {
      const threshold = LOCKOUT_THRESHOLDS[i];
      if (attempts >= threshold.attempts) {
        if (threshold.lockoutMs === null) {
          // 10+ intentos → forzar logout
          sessionStorage.removeItem(FAILED_ATTEMPTS_KEY);
          sessionStorage.removeItem(LOCKOUT_UNTIL_KEY);
          setMustLogout(true);
          return;
        }
        const until = Date.now() + threshold.lockoutMs;
        sessionStorage.setItem(LOCKOUT_UNTIL_KEY, until.toString());
        setLockoutUntil(until);
        // Auto-clear lockout cuando expire
        if (lockoutTimerRef.current) clearTimeout(lockoutTimerRef.current);
        lockoutTimerRef.current = setTimeout(() => {
          setLockoutUntil(null);
          sessionStorage.removeItem(LOCKOUT_UNTIL_KEY);
        }, threshold.lockoutMs);
        return;
      }
    }
  }, []);

  // Re-arm lockout timer on mount if active lockout was restored from sessionStorage
  useEffect(() => {
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remaining = lockoutUntil - Date.now();
      lockoutTimerRef.current = setTimeout(() => {
        setLockoutUntil(null);
        sessionStorage.removeItem(LOCKOUT_UNTIL_KEY);
      }, remaining);
    }
    return () => {
      if (lockoutTimerRef.current) clearTimeout(lockoutTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intentar desbloquear con PIN
  const unlock = async (pin) => {
    // Bloquear si está en lockout
    if (lockoutUntil && Date.now() < lockoutUntil) {
      return { success: false, locked: true };
    }

    const savedPin = storage.get(PIN_STORAGE_KEY);
    let matches = false;
    let needsMigration = false;

    if (isPBKDF2Format(savedPin)) {
      // Current format: salt:hash — verify with same salt
      const [salt] = savedPin.split(':');
      const derived = await hashPin(pin, salt);
      matches = derived === savedPin;
    } else if (savedPin && savedPin.length === PIN_HASH_LENGTH) {
      // Legacy SHA-256 hash — compare and migrate
      const hashedInput = await hashPinLegacy(pin);
      matches = hashedInput === savedPin;
      needsMigration = matches;
    } else if (savedPin) {
      // Very old plaintext — compare and migrate
      matches = pin === savedPin;
      needsMigration = matches;
    }

    if (matches) {
      // Migrate legacy formats to PBKDF2
      if (needsMigration) {
        const saltedHash = await hashPin(pin);
        storage.set(PIN_STORAGE_KEY, saltedHash);
        console.log('[LockContext] PIN migrado a PBKDF2 con salt');
      }
      console.log('[LockContext] PIN correcto, desbloqueando');
      // Generate signed unlock token using stored/new hash
      const currentHash = storage.get(PIN_STORAGE_KEY);
      const { nonce, token } = await generateUnlockToken(currentHash);
      storage.set(UNLOCK_TOKEN_KEY, token);
      storage.set(UNLOCK_NONCE_KEY, nonce);
      storage.set(LOCK_TIMESTAMP_KEY, Date.now().toString());
      setIsLocked(false);
      setFailedAttempts(0);
      setLockoutUntil(null);
      sessionStorage.removeItem(FAILED_ATTEMPTS_KEY);
      sessionStorage.removeItem(LOCKOUT_UNTIL_KEY);
      if (lockoutTimerRef.current) clearTimeout(lockoutTimerRef.current);
      return { success: true };
    } else {
      const newAttempts = failedAttempts + 1;
      console.log(`[LockContext] PIN incorrecto (intento ${newAttempts})`);
      setFailedAttempts(newAttempts);
      applyLockout(newAttempts);
      return { success: false, attempts: newAttempts };
    }
  };

  // Cambiar PIN (requiere PIN actual)
  const changePin = async (currentPin, newPin) => {
    const savedPin = storage.get(PIN_STORAGE_KEY);
    let currentMatches = false;

    if (isPBKDF2Format(savedPin)) {
      const [salt] = savedPin.split(':');
      const derived = await hashPin(currentPin, salt);
      currentMatches = derived === savedPin;
    } else if (savedPin && savedPin.length === PIN_HASH_LENGTH) {
      const hashedCurrent = await hashPinLegacy(currentPin);
      currentMatches = hashedCurrent === savedPin;
    } else if (savedPin) {
      currentMatches = currentPin === savedPin;
    }

    if (!currentMatches) {
      const err = new Error('PIN actual incorrecto');
      err.code = 'INCORRECT_CURRENT_PIN';
      throw err;
    }
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      throw new Error('El nuevo PIN debe tener exactamente 4 dígitos');
    }
    const saltedHash = await hashPin(newPin);
    storage.set(PIN_STORAGE_KEY, saltedHash);
    storage.set(PIN_CONFIGURED_KEY, 'true');
    // Re-sign unlock token with new hash
    const { nonce, token } = await generateUnlockToken(saltedHash);
    storage.set(UNLOCK_TOKEN_KEY, token);
    storage.set(UNLOCK_NONCE_KEY, nonce);
    storage.set(LOCK_TIMESTAMP_KEY, Date.now().toString());
    console.log('[LockContext] PIN cambiado exitosamente (PBKDF2)');
  };

  const value = {
    isLocked,
    hasPin,
    setPin,
    lock,
    unlock,
    changePin,
    failedAttempts,
    lockoutUntil,
    mustLogout,
  };

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>;
};
